import { useCallback } from 'react';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import { fireCmdbErrorAnalytics } from '@atlassian/jira-servicedesk-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge/src/utils/fire-analytics';
import { performCmdbGetRequest } from '@atlassian/jira-servicedesk-cmdb-client/src/fetch.tsx';
import { getCmdbConfigStatusTypePickerUrl } from '@atlassian/jira-servicedesk-cmdb-rest-urls/src/common/constants/config.tsx';
import type { ConfigStatusTypePickerRequest } from '@atlassian/jira-servicedesk-cmdb-rest-urls/src/common/types/config.tsx';
import { useWorkspaceContext } from '@atlassian/jira-servicedesk-insight-common-workspace-context/src/controllers/workspace-context/index.tsx';
import type { Status } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-details.tsx';

export type StatusSearchResponse = {
	header: string;
	statuses: Status[];
	total: number;
};

const MAX_RESULTS = 10;

export const fetchStatuses = async ({
	workspaceId,
	objectSchemaId,
	objectTypeMultiValues,
	query,
}: {
	workspaceId: string;
	objectSchemaId: string;
	objectTypeMultiValues: string[] | undefined;
	query: string;
}): Promise<StatusSearchResponse> => {
	const queryParams: ConfigStatusTypePickerRequest = {
		query,
		objectSchemaId,
		maxresult: MAX_RESULTS,
		ids: objectTypeMultiValues ? objectTypeMultiValues.join(',') : '',
	};
	const { header, statuses, total }: StatusSearchResponse = await performCmdbGetRequest(
		getCmdbConfigStatusTypePickerUrl(workspaceId, queryParams),
	);
	return { header, statuses, total };
};

export const useStatusesSearch = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { workspaceId } = useWorkspaceContext();

	const statusesSearch = useCallback(
		async ({
			objectSchemaId,
			objectTypeMultiValues,
			query,
		}: {
			objectSchemaId: string;
			objectTypeMultiValues: string[] | undefined;
			query: string;
		}): Promise<StatusSearchResponse | undefined> => {
			if (workspaceId) {
				try {
					const response = await fetchStatuses({
						workspaceId,
						objectSchemaId,
						objectTypeMultiValues,
						query,
					});
					fireTrackAnalytics(createAnalyticsEvent({}), 'insightSearchStatuses succeeded');
					return response;
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					fireCmdbErrorAnalytics({
						error,
						meta: {
							id: 'insightSearchStatuses',
							teamName: 'falcons',
							packageName: 'jiraServicedeskInsightStatusPicker',
						},
					});
					throw error;
				}
			}
			return undefined;
		},
		[createAnalyticsEvent, workspaceId],
	);

	return useService<
		StatusSearchResponse,
		{
			objectSchemaId: string;
			objectTypeMultiValues: string[] | undefined;
			query: string;
		}
	>(statusesSearch);
};
